import { Alert, Box, CircularProgress } from '@mui/material';
import React from 'react';
import InputMask from 'react-input-mask';
import PasswordStrengthMeter from '../common/PasswordStrengthMeter';
import { KitButton } from '../common/MobileKit';
import { KitField, KitInput, KitInputButton, KitInputIco, KitSelect, KitSeg } from '../common/MobileKitExtras';
import { protoIcon } from '../common/ProtoIcon';
import logoMark from '../../img/logo-branco.png';
import { LEGAL_LINKS_ENABLED } from '../../pages/legal/legalLinks';
import { AUTH_ICON_BTN_SX, AuthCheck, MobileAuthPage, authLinkSx } from './MobileAuth';

/**
 * Login / Criar conta no mobile — `prototipos-mobile/auth/login.html` (abas
 * Entrar / Criar conta). Só apresentação: estado, validações, máscara e
 * chamadas de API ficam em `pages/Login.js` (props).
 */
const Lock = protoIcon('lock');
const Eye = protoIcon('eye');
const EyeOff = protoIcon('eyeoff');
const UserIcon = protoIcon('user');
const Grid = protoIcon('grid');
const Moon = protoIcon('moon');
const Sun = protoIcon('sun');

const LegalLink = ({ href, children }) => (LEGAL_LINKS_ENABLED
    ? <Box component="a" href={href} target="_blank" rel="noopener noreferrer" sx={authLinkSx}>{children}</Box>
    : <Box component="span" aria-disabled="true" tabIndex={-1} sx={{ ...authLinkSx, color: 'inherit', cursor: 'default' }}>{children}</Box>);

export default function MobileLogin({
    isLoginMode, onToggleMode, loginFormik, registerFormik, errorInvalidCredentials, registerSuccess, registerError,
    isLoading, registerLoading, showLoginPassword, setShowLoginPassword, showRegisterPassword, setShowRegisterPassword,
    showConfirmPassword, setShowConfirmPassword, termsAccepted, setTermsAccepted, onForgotPassword, mode, onToggleTheme,
}) {
    const fe = (formik, name) => formik.errors[name];
    const eye = (shown, set) => (
        <KitInputButton aria-label="alternar visibilidade da senha" onClick={() => set(!shown)}>
            {shown ? <EyeOff size={20} /> : <Eye size={20} />}
        </KitInputButton>
    );
    const confirmPasswordMatches = registerFormik.values.confirmPassword.length > 0 && !registerFormik.errors.confirmPassword;
    const alertSx = { mb: '14px', borderRadius: '14px', fontSize: 13 };

    return (
        <MobileAuthPage noBar>
            <Box component="button" type="button" onClick={onToggleTheme} aria-label="Alternar tema" sx={{ ...AUTH_ICON_BTN_SX, position: 'absolute', top: 'calc(8px + env(safe-area-inset-top, 0px))', right: '12px' }}>
                {mode === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
            </Box>

            <Box sx={{ textAlign: 'center', m: '28px 0 22px' }}>
                <Box sx={{ width: 64, height: 64, borderRadius: '20px', backgroundImage: (t) => t.palette.gradient.brand, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Box component="img" src={logoMark} alt="FlowVendas" sx={{ width: 38, height: 38, objectFit: 'contain', display: 'block' }} />
                </Box>
                <Box sx={{ fontWeight: 800, fontSize: 22, letterSpacing: '-.02em', mt: '12px' }}>FlowVendas</Box>
                <Box sx={{ color: 'text.disabled', fontSize: 12.5 }}>Gestão simples para o seu negócio</Box>
            </Box>

            <KitSeg
                options={[{ value: 'login', label: 'Entrar' }, { value: 'cadastro', label: 'Criar conta' }]}
                value={isLoginMode ? 'login' : 'cadastro'}
                onChange={onToggleMode}
            />

            <Box sx={{ mt: '16px' }}>
                {errorInvalidCredentials && <Alert severity="error" sx={alertSx}>Credenciais inválidas. Verifique seus dados e tente novamente.</Alert>}
                {registerSuccess && <Alert severity="success" sx={alertSx}>Cadastro realizado! Um e-mail foi enviado para ativação da sua conta.</Alert>}
                {registerError && <Alert severity="error" sx={alertSx}>{registerError}</Alert>}

                {isLoginMode ? (
                    <Box component="form" onSubmit={loginFormik.handleSubmit} noValidate>
                        <KitField label="Código do cliente" help={fe(loginFormik, 'codeClient')} error>
                            <KitInputIco icon={Grid}>
                                <KitInput name="codeClient" autoFocus error={!!fe(loginFormik, 'codeClient')} value={loginFormik.values.codeClient} onChange={(e) => loginFormik.setFieldValue('codeClient', e.target.value)} sx={{ pl: '44px' }} />
                            </KitInputIco>
                        </KitField>
                        <KitField label="Usuário" help={fe(loginFormik, 'username')} error>
                            <KitInputIco icon={UserIcon}>
                                <KitInput name="username" autoComplete="username" error={!!fe(loginFormik, 'username')} value={loginFormik.values.username} onChange={(e) => loginFormik.setFieldValue('username', e.target.value)} sx={{ pl: '44px' }} />
                            </KitInputIco>
                        </KitField>
                        <KitField label="Senha" help={fe(loginFormik, 'password')} error>
                            <KitInputIco icon={Lock} rightButtons={eye(showLoginPassword, setShowLoginPassword)}>
                                <KitInput name="password" type={showLoginPassword ? 'text' : 'password'} autoComplete="current-password" placeholder="••••••••" error={!!fe(loginFormik, 'password')} value={loginFormik.values.password} onChange={(e) => loginFormik.setFieldValue('password', e.target.value)} sx={{ pl: '44px', pr: '48px' }} />
                            </KitInputIco>
                        </KitField>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', m: '-4px 0 18px' }}>
                            <Box component="button" type="button" onClick={onForgotPassword} sx={authLinkSx}>Esqueci a senha</Box>
                        </Box>
                        <KitButton type="submit" variant="primary" block disabled={isLoading}>
                            {isLoading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Entrar'}
                        </KitButton>
                    </Box>
                ) : (
                    <Box component="form" onSubmit={registerFormik.handleSubmit} noValidate>
                        <KitField label="Nome da empresa" required help={fe(registerFormik, 'codEnterprise')} error>
                            <KitInput name="codEnterprise" placeholder="Auto Center Silva" error={!!fe(registerFormik, 'codEnterprise')} value={registerFormik.values.codEnterprise} onChange={(e) => registerFormik.setFieldValue('codEnterprise', e.target.value)} />
                        </KitField>
                        <KitField label="Segmento" required help={fe(registerFormik, 'segment')} error>
                            <KitSelect name="segment" error={!!fe(registerFormik, 'segment')} value={registerFormik.values.segment} onChange={(e) => registerFormik.setFieldValue('segment', e.target.value)}>
                                <option value="" disabled>Selecione o segmento</option>
                                <option value="bussiness">Comércio</option>
                                <option value="assistance">Assistência ou Prestação de Serviços</option>
                                <option value="workshop">Oficina Mecânica</option>
                            </KitSelect>
                        </KitField>
                        <KitField label="Nome" required help={fe(registerFormik, 'name')} error>
                            <KitInput name="name" placeholder="João" error={!!fe(registerFormik, 'name')} value={registerFormik.values.name} onChange={(e) => registerFormik.setFieldValue('name', e.target.value)} />
                        </KitField>
                        <KitField label="Sobrenome" required help={fe(registerFormik, 'lastName')} error>
                            <KitInput name="lastName" placeholder="Silva" error={!!fe(registerFormik, 'lastName')} value={registerFormik.values.lastName} onChange={(e) => registerFormik.setFieldValue('lastName', e.target.value)} />
                        </KitField>
                        <KitField label="E-mail" required help={fe(registerFormik, 'email')} error>
                            <KitInput name="email" type="email" placeholder="voce@empresa.com.br" error={!!fe(registerFormik, 'email')} value={registerFormik.values.email} onChange={(e) => registerFormik.setFieldValue('email', e.target.value)} />
                        </KitField>
                        <KitField label="Celular" required help={fe(registerFormik, 'cellphone')} error>
                            <InputMask mask="(99) 99999-9999" value={registerFormik.values.cellphone} onChange={(e) => registerFormik.setFieldValue('cellphone', e.target.value)}>
                                {() => <KitInput name="cellphone" type="tel" placeholder="(11) 99999-0000" error={!!fe(registerFormik, 'cellphone')} />}
                            </InputMask>
                        </KitField>
                        <KitField label="Senha" required help={fe(registerFormik, 'password')} error>
                            <KitInputIco rightButtons={eye(showRegisterPassword, setShowRegisterPassword)}>
                                <KitInput name="password" type={showRegisterPassword ? 'text' : 'password'} placeholder="Mínimo 6 caracteres" error={!!fe(registerFormik, 'password')} value={registerFormik.values.password} onChange={(e) => registerFormik.setFieldValue('password', e.target.value)} sx={{ pr: '48px' }} />
                            </KitInputIco>
                        </KitField>
                        <PasswordStrengthMeter
                            password={registerFormik.values.password}
                            emptyLabel="Use letras, números e símbolos"
                            showChecklist={false}
                            sx={{ mt: '-4px', mb: '14px', '& > div:first-of-type': { height: 8, mt: 0, mb: '6px' }, '& > p': { fontSize: 12 } }}
                        />
                        <KitField label="Confirmar senha" required help={confirmPasswordMatches ? undefined : fe(registerFormik, 'confirmPassword')} error>
                            <KitInputIco rightButtons={eye(showConfirmPassword, setShowConfirmPassword)}>
                                <KitInput name="confirmPassword" type={showConfirmPassword ? 'text' : 'password'} placeholder="Repita a senha" error={!!fe(registerFormik, 'confirmPassword') && !confirmPasswordMatches} value={registerFormik.values.confirmPassword} onChange={(e) => registerFormik.setFieldValue('confirmPassword', e.target.value)} sx={{ pr: '48px' }} />
                            </KitInputIco>
                        </KitField>
                        <AuthCheck checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)}>
                            <Box component="span" sx={{ fontSize: 12.5, lineHeight: 1.4 }}>
                                Li e aceito os <LegalLink href="/termos-de-uso">Termos de uso</LegalLink> e a <LegalLink href="/politica-de-privacidade">Política de privacidade</LegalLink>
                            </Box>
                        </AuthCheck>
                        <KitButton type="submit" variant="primary" block disabled={registerLoading || !termsAccepted}>
                            {registerLoading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Criar minha conta'}
                        </KitButton>
                    </Box>
                )}
            </Box>

            <Box sx={{ textAlign: 'center', mt: '22px', fontSize: 11.5, color: 'text.disabled' }}>Sem cartão de crédito · Cancele quando quiser</Box>
        </MobileAuthPage>
    );
}

